'use client';

import { useMemo } from 'react';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useCellServiceSession } from '@/hooks/useCellServiceSession';
import {
  mobileGraphql,
  mobileKeys,
  type MobileConnection,
  type MobileUiConfigResponse,
} from '@/shared/mobile-service/api/cors-mobile-api';
import { buildArtifactRecordQuery } from '../lib/build-record-query';
import { resolveArtifactColumns } from '../lib/columns';
import type { ArtifactRow } from './useArtifactList';

/**
 * Fetch a single artifact row by id for the record panel.
 *
 * Selection lives in the URL, so the panel can be opened on a row that isn't on
 * the current page (or in the cache at all) — a shared deep link, or a jump from
 * an assistant answer. Fetching by id rather than reading the list cache is what
 * makes those entry points work.
 */
export function useArtifactRecord(args: {
  mobileAcquisitionId: number;
  graphqlType: string;
  recordId: string | null;
  uiConfig: MobileUiConfigResponse | undefined;
}): UseQueryResult<ArtifactRow | null, Error> {
  const { mobileAcquisitionId, graphqlType, recordId, uiConfig } = args;
  const { cellId, accessToken, enabled } = useCellServiceSession();

  const artifact = uiConfig?.artifacts[graphqlType];

  const built = useMemo(() => {
    if (!uiConfig || !artifact || !recordId) return null;
    const resolved = resolveArtifactColumns(graphqlType, uiConfig);
    if (!resolved) return null;
    return buildArtifactRecordQuery(resolved, artifact.endpoint, uiConfig, {
      mobileAcquisitionId,
      recordId,
    });
  }, [uiConfig, artifact, graphqlType, mobileAcquisitionId, recordId]);

  return useQuery<ArtifactRow | null, Error>({
    queryKey: mobileKeys.artifactRecord(
      cellId,
      mobileAcquisitionId,
      graphqlType,
      recordId ?? '',
    ),
    enabled: enabled && Boolean(built),
    queryFn: async ({ signal }) => {
      const data = await mobileGraphql<Record<string, MobileConnection<ArtifactRow>>>({
        cellId: cellId as string,
        accessToken: accessToken as string,
        endpoint: (built as NonNullable<typeof built>).endpoint,
        query: (built as NonNullable<typeof built>).query,
        signal,
      });
      const connection = data[(built as NonNullable<typeof built>).rootQuery];
      return connection?.items?.[0] ?? null;
    },
  });
}
